import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@shadcn/ui/alert-dialog";
import { Button } from "@shadcn/ui/button";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@shadcn/ui/drawer";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { stripePromise } from "@utils/stripe";
import { LoaderCircle } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { usePreloadedQuery, useRelayEnvironment } from "react-relay";
import { fetchQuery, graphql } from "relay-runtime";
import { toast } from "sonner";
import z from "zod";
import { AppHeader } from "~/components/layout/AppHeader";
import { BackLink } from "~/components/one-delivery/BackLink";
import { CardFrame, CardHeading, Feature, OfferCard, PlanCard, useFormatPrice } from "~/components/one-delivery/CreditCards";
import { PageContainer } from "~/components/layout/PageContainer";
import { requireOnboarded } from "~/components/one-delivery/onboarding";
import type { creditsOneDeliveryPurchaseMutation } from "./__generated__/creditsOneDeliveryPurchaseMutation.graphql";
import type { creditsOneDeliveryPurchaseStatusQuery } from "./__generated__/creditsOneDeliveryPurchaseStatusQuery.graphql";
import type { creditsOneDeliveryQuery, creditsOneDeliveryQuery$data } from "./__generated__/creditsOneDeliveryQuery.graphql";
import type { creditsOneDeliverySubscribeMutation } from "./__generated__/creditsOneDeliverySubscribeMutation.graphql";
import type { creditsOneDeliverySubscriptionCancelMutation } from "./__generated__/creditsOneDeliverySubscriptionCancelMutation.graphql";
import type { creditsOneDeliverySubscriptionResumeMutation } from "./__generated__/creditsOneDeliverySubscriptionResumeMutation.graphql";
import type { creditsOneDeliverySubscriptionStatusQuery } from "./__generated__/creditsOneDeliverySubscriptionStatusQuery.graphql";

const query = graphql`
	query creditsOneDeliveryQuery {
		creditBalance {
			available
			expiring
			nextExpiryAt
		}
		creditSubscription {
			id
			status
			cancelAtPeriodEnd
			currentPeriodEnd
			plan {
				id
				title
				credits
				priceCents
				active
			}
		}
		creditPlans {
			id
			title
			description
			features
			credits
			priceCents
		}
		creditOffers {
			id
			title
			description
			features
			credits
			priceCents
			maxPerUser
			purchasable
		}
	}
`;

const purchaseMutation = graphql`
	mutation creditsOneDeliveryPurchaseMutation($offerId: UUID!) {
		creditPurchaseCreate(input: { offerId: $offerId }) {
			clientSecret
			intentKind
			purchase {
				id
				status
				credits
			}
		}
	}
`;

const purchaseStatusQuery = graphql`
	query creditsOneDeliveryPurchaseStatusQuery($id: UUID!) {
		creditPurchase(id: $id) {
			id
			status
			credits
		}
	}
`;

const subscribeMutation = graphql`
	mutation creditsOneDeliverySubscribeMutation($planId: UUID!, $manageUrl: String) {
		creditSubscriptionCreate(input: { planId: $planId, manageUrl: $manageUrl }) {
			clientSecret
			subscription {
				id
				status
			}
		}
	}
`;

const subscriptionStatusQuery = graphql`
	query creditsOneDeliverySubscriptionStatusQuery {
		creditSubscription {
			id
			status
			plan {
				id
				credits
			}
		}
	}
`;

const cancelMutation = graphql`
	mutation creditsOneDeliverySubscriptionCancelMutation {
		creditSubscriptionCancel {
			id
			status
			cancelAtPeriodEnd
			currentPeriodEnd
		}
	}
`;

const resumeMutation = graphql`
	mutation creditsOneDeliverySubscriptionResumeMutation {
		creditSubscriptionResume {
			id
			status
			cancelAtPeriodEnd
			currentPeriodEnd
		}
	}
`;

export const Route = createFileRoute("/one-delivery/credits")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery Credits";
	},
	component: RouteComponent,
	// Stripe returns here after a redirect-based payment method.
	validateSearch: zodValidator(
		z.object({
			purchaseId: z.string().optional(),
			subscription: z.boolean().optional(),
			onboarding: z.boolean().optional(),
			bundleId: z.string().optional(),
			// The offer or plan chosen in the onboarding: its checkout starts right away.
			offerId: z.string().optional(),
			planId: z.string().optional(),
		})
	),
	loader: async ({ context }) => {
		if (!(await checkSession())) {
			throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery/credits", show: "sign-in" } });
		}
		await requireOnboarded(context.relayEnvironment);
		return preloadQuery<creditsOneDeliveryQuery>(context.relayEnvironment, query, {}, { fetchPolicy: "network-only" });
	},
});

type Offer = creditsOneDeliveryQuery$data["creditOffers"][number];
type Plan = creditsOneDeliveryQuery$data["creditPlans"][number];
type Subscription = NonNullable<creditsOneDeliveryQuery$data["creditSubscription"]>;
/**
 * An intent to confirm with the PaymentElement: a credit purchase or a subscription invoice
 * payment, or the SetupIntent of a free offer, which only saves the user's card.
 */
type Checkout = {
	clientSecret: string;
	intent: "PAYMENT" | "SETUP";
	title: string;
	description: string;
	purchaseId?: string;
	offerId?: string;
};

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 60_000;
/** Subscription statuses whose payment the user can still confirm. */
const PAYMENT_DUE = ["INCOMPLETE", "PAST_DUE", "UNPAID"];

function useFormatDate() {
	const { locale } = useI18nContext();
	return (value: string) => new Date(value).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const { locale } = useI18nContext();
	const router = useRouter();
	const navigate = useNavigate();
	const environment = useRelayEnvironment();
	const formatDate = useFormatDate();
	const {
		purchaseId: returnedPurchaseId,
		subscription: returnedSubscription,
		onboarding,
		bundleId,
		offerId: chosenOfferId,
		planId: chosenPlanId,
	} = Route.useSearch();
	const { creditBalance, creditOffers, creditPlans, creditSubscription } = usePreloadedQuery<creditsOneDeliveryQuery>(
		query,
		Route.useLoaderData()
	);
	const [purchase, isPurchasing] = useAsyncMutation<creditsOneDeliveryPurchaseMutation>(purchaseMutation);
	const [subscribe, isSubscribing] = useAsyncMutation<creditsOneDeliverySubscribeMutation>(subscribeMutation);
	const [pendingId, setPendingId] = useState<string | null>(null);
	const [checkout, setCheckout] = useState<Checkout | null>(null);
	const [confirming, setConfirming] = useState(false);
	// The offer or plan whose payment is being confirmed (unknown after a redirect-based return).
	const [confirmingItemId, setConfirmingItemId] = useState<string | null>(null);

	const completed = async (message: string) => {
		toast.success(message);
		// The first Credits complete the One Delivery onboarding.
		if (onboarding) return void (await navigate({ to: "/one-delivery/onboarding/finish" }));
		// Bought from a bundle that lacked Credits: go back to send it.
		if (bundleId) return void (await navigate({ to: "/one-delivery/bundle/$bundleId", params: { bundleId } }));
		await router.invalidate();
	};

	/** Polls `check` until it reports an outcome, e.g. until the webhook granted the Credits. */
	const waitFor = async (itemId: string | null, check: () => Promise<"done" | "canceled" | "pending">, onDone: () => Promise<void>) => {
		setConfirming(true);
		setConfirmingItemId(itemId);
		const deadline = Date.now() + POLL_TIMEOUT_MS;
		try {
			while (Date.now() < deadline) {
				const outcome = await check();
				if (outcome === "done") return await onDone();
				if (outcome === "canceled") return void toast.error(i18n.OD_CREDITS_PAYMENT_CANCELED());
				await sleep(POLL_INTERVAL_MS);
			}
			toast.info(i18n.OD_CREDITS_PAYMENT_DELAYED());
		} catch (error) {
			console.error(error);
			toast.info(i18n.OD_CREDITS_PAYMENT_DELAYED());
		} finally {
			setConfirming(false);
			setConfirmingItemId(null);
		}
	};

	const waitForPurchase = (id: string, offerId?: string) => {
		let credits = 0;
		return waitFor(
			offerId ?? null,
			async () => {
				const data = await fetchQuery<creditsOneDeliveryPurchaseStatusQuery>(
					environment,
					purchaseStatusQuery,
					{ id },
					{ fetchPolicy: "network-only" }
				).toPromise();
				credits = data?.creditPurchase.credits ?? 0;
				const status = data?.creditPurchase.status;
				return status === "COMPLETED" ? "done" : status === "CANCELED" ? "canceled" : "pending";
			},
			() => completed(i18n.OD_CREDITS_ADDED({ count: credits }))
		);
	};

	/** Waits until Stripe reports the subscription's payment, which also grants the month's Credits. */
	const waitForSubscription = (planId?: string) => {
		let credits = 0;
		return waitFor(
			planId ?? null,
			async () => {
				const data = await fetchQuery<creditsOneDeliverySubscriptionStatusQuery>(
					environment,
					subscriptionStatusQuery,
					{},
					{ fetchPolicy: "network-only" }
				).toPromise();
				const subscription = data?.creditSubscription;
				if (!subscription) return "canceled";
				credits = subscription.plan.credits;
				return PAYMENT_DUE.includes(subscription.status) ? "pending" : "done";
			},
			() => completed(i18n.OD_SUBS_ACTIVE({ count: credits }))
		);
	};

	useEffect(() => {
		if (!returnedPurchaseId && !returnedSubscription) return;
		void navigate({ to: "/one-delivery/credits", search: { onboarding, bundleId }, replace: true });
		if (returnedPurchaseId) void waitForPurchase(returnedPurchaseId);
		else void waitForSubscription();
	}, [returnedPurchaseId, returnedSubscription]);

	const busy = isPurchasing || isSubscribing || !!pendingId || confirming;

	const buy = async (offer: Offer) => {
		if (busy) return;
		setPendingId(offer.id);
		try {
			const { creditPurchaseCreate: result } = await purchase({ variables: { offerId: offer.id } });
			if (result.purchase.status === "COMPLETED") await completed(i18n.OD_CREDITS_ADDED({ count: result.purchase.credits }));
			else if (result.clientSecret) {
				const intent = result.intentKind === "SETUP" ? "SETUP" : "PAYMENT";
				setCheckout({
					clientSecret: result.clientSecret,
					intent,
					title:
						intent === "SETUP"
							? i18n.OD_CREDITS_SETUP_TITLE({ title: offer.title })
							: i18n.OD_CREDITS_PAYMENT_TITLE({ title: offer.title }),
					description: i18n.OD_CREDITS_COUNT({ count: offer.credits }),
					purchaseId: result.purchase.id,
					offerId: offer.id,
				});
			} else void waitForPurchase(result.purchase.id, offer.id);
		} catch (error) {
			toast.error(i18n.OD_CREDITS_FAILED(), { description: error instanceof Error ? error.message : undefined });
		} finally {
			setPendingId(null);
		}
	};

	/** Subscribes to a plan, or pays the invoice due on the current subscription of that plan. */
	const startSubscription = async (plan: Pick<Plan, "id" | "title" | "credits">) => {
		if (busy) return;
		setPendingId(plan.id);
		try {
			const manageUrl = new URL("/one-delivery/credits", window.location.origin).toString();
			const { creditSubscriptionCreate: result } = await subscribe({ variables: { planId: plan.id, manageUrl } });
			if (result.clientSecret)
				setCheckout({
					clientSecret: result.clientSecret,
					intent: "PAYMENT",
					title: i18n.OD_SUBS_PAYMENT_TITLE({ title: plan.title }),
					description: i18n.OD_SUBS_COUNT({ count: plan.credits }),
					offerId: plan.id,
				});
			else void waitForSubscription(plan.id);
		} catch (error) {
			toast.error(i18n.OD_CREDITS_FAILED(), { description: error instanceof Error ? error.message : undefined });
		} finally {
			setPendingId(null);
		}
	};

	const startedChoice = useRef(false);
	useEffect(() => {
		if (startedChoice.current || (!chosenOfferId && !chosenPlanId)) return;
		startedChoice.current = true;
		void navigate({ to: "/one-delivery/credits", search: { onboarding, bundleId }, replace: true });
		const offer = creditOffers.find((item) => item.id === chosenOfferId && item.purchasable);
		const plan = creditSubscription ? undefined : creditPlans.find((item) => item.id === chosenPlanId);
		if (offer) void buy(offer);
		else if (plan) void startSubscription(plan);
	}, [chosenOfferId, chosenPlanId]);

	return (
		<PageContainer className="bg-slate-50">
			<AppHeader />
			{bundleId ? (
				<BackLink to="/one-delivery/bundle/$bundleId" params={{ bundleId }} label={i18n.OD_BACK_TO_BUNDLE()} />
			) : (
				<BackLink to="/one-delivery" label={i18n.OD_BACK_TO_SHIPMENTS()} />
			)}
			<main className="flex flex-col gap-4 px-6 pt-2 pb-8">
				<div className="flex flex-col gap-1 py-2">
					<h1 className="text-2xl font-breit font-normal text-neutral-900">
						<T>{i18n.OD_CREDITS_TITLE()}</T>
					</h1>
					<p className="text-sm text-muted-foreground">{i18n.OD_CREDITS_SUBTITLE()}</p>
				</div>

				<div className="flex flex-col gap-1 rounded-[14px] border border-neutral-200 bg-white px-6 py-4 shadow-sm">
					<div className="flex items-center justify-between">
						<span className="text-sm text-muted-foreground">{i18n.OD_CREDITS_BALANCE()}</span>
						<span className="text-3xl font-medium tabular-nums text-neutral-950">{creditBalance.available}</span>
					</div>
					{creditBalance.expiring > 0 && creditBalance.nextExpiryAt && (
						<span className="text-xs text-muted-foreground">
							{i18n.OD_SUBS_EXPIRING({ count: creditBalance.expiring, date: formatDate(creditBalance.nextExpiryAt) })}
						</span>
					)}
				</div>

				{confirming && (
					<div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm">
						<LoaderCircle className="size-4 animate-spin" />{" "}
						{creditOffers.some((offer) => offer.id === confirmingItemId && offer.priceCents === 0)
							? i18n.OD_CREDITS_SETUP_PROCESSING()
							: i18n.OD_CREDITS_PROCESSING()}
					</div>
				)}

				{(creditSubscription || creditPlans.length > 0) && (
					<section className="flex flex-col gap-4">
						<div className="flex flex-col gap-1 pt-2">
							<h2 className="text-lg font-medium text-neutral-950">{i18n.OD_SUBS_TITLE()}</h2>
							<p className="text-sm text-muted-foreground">{i18n.OD_SUBS_SUBTITLE()}</p>
						</div>
						{creditSubscription && (
							<SubscriptionCard
								subscription={creditSubscription}
								loading={pendingId === creditSubscription.plan.id || confirmingItemId === creditSubscription.plan.id}
								disabled={busy}
								onPay={() => void startSubscription(creditSubscription.plan)}
							/>
						)}
						{/* One open subscription per user: another plan can be chosen only once it has ended. */}
						{creditSubscription && creditPlans.some((plan) => plan.id !== creditSubscription.plan.id) && (
							<p className="text-sm text-muted-foreground">{i18n.OD_SUBS_SWITCH_HINT()}</p>
						)}
						{creditPlans
							.filter((plan) => plan.id !== creditSubscription?.plan.id)
							.map((plan) => (
								<SubscribablePlan
									key={plan.id}
									plan={plan}
									available={!creditSubscription}
									loading={pendingId === plan.id || confirmingItemId === plan.id}
									disabled={busy}
									onSubscribe={() => void startSubscription(plan)}
								/>
							))}
					</section>
				)}

				<section className="flex flex-col gap-4">
					{(creditSubscription || creditPlans.length > 0) && (
						<h2 className="pt-2 text-lg font-medium text-neutral-950">{i18n.OD_ONE_TIME_TITLE()}</h2>
					)}
					{creditOffers.length === 0 && <p className="text-center text-sm text-muted-foreground">{i18n.OD_CREDITS_EMPTY()}</p>}
					{/* Offers that can no longer be bought (e.g. already redeemed) always go last. */}
					{creditOffers
						.toSorted((a, b) => Number(!a.purchasable) - Number(!b.purchasable))
						.map((offer) => (
							<BuyableOffer
								key={offer.id}
								offer={offer}
								loading={pendingId === offer.id || confirmingItemId === offer.id}
								disabled={busy}
								onBuy={() => void buy(offer)}
							/>
						))}
				</section>
			</main>

			<Drawer open={!!checkout} onOpenChange={(open) => !open && setCheckout(null)}>
				<DrawerContent>
					{checkout && (
						<div className="mx-auto flex min-h-0 w-full max-w-[390px] flex-col gap-4 overflow-y-auto px-6 pb-8">
							<DrawerHeader className="px-0">
								<DrawerTitle>{checkout.title}</DrawerTitle>
								<DrawerDescription>{checkout.description}</DrawerDescription>
							</DrawerHeader>
							{checkout.intent === "SETUP" && (
								<p className="rounded-lg bg-neutral-100 px-4 py-3 text-sm text-neutral-700">{i18n.OD_CREDITS_SETUP_NOTE()}</p>
							)}
							<Elements stripe={stripePromise} options={{ clientSecret: checkout.clientSecret, locale }}>
								<PaymentForm
									intent={checkout.intent}
									returnParams={{
										...(checkout.purchaseId ? { purchaseId: checkout.purchaseId } : { subscription: "true" }),
										...(onboarding ? { onboarding: "true" } : {}),
										...(bundleId ? { bundleId } : {}),
									}}
									onPaid={() => {
										const { purchaseId, offerId } = checkout;
										setCheckout(null);
										void (purchaseId ? waitForPurchase(purchaseId, offerId) : waitForSubscription(offerId));
									}}
								/>
							</Elements>
						</div>
					)}
				</DrawerContent>
			</Drawer>
		</PageContainer>
	);
}

function BuyableOffer({ offer, loading, disabled, onBuy }: { offer: Offer; loading: boolean; disabled: boolean; onBuy: () => void }) {
	const i18n = useI18nContext().LL["new-ui"];
	const formatPrice = useFormatPrice();
	const label = !offer.purchasable
		? i18n.OD_CREDITS_UNAVAILABLE()
		: offer.priceCents === 0
			? i18n.OD_CREDITS_REDEEM()
			: i18n.OD_CREDITS_PURCHASE({ price: formatPrice(offer.priceCents) });
	return (
		<OfferCard
			offer={offer}
			action={
				<Button size="lg" className="w-full font-breit font-normal" disabled={!offer.purchasable || disabled || loading} onClick={onBuy}>
					{loading && <LoaderCircle className="size-4 animate-spin" />}
					{label}
				</Button>
			}
		/>
	);
}

function SubscribablePlan({
	plan,
	available,
	loading,
	disabled,
	onSubscribe,
}: {
	plan: Plan;
	available: boolean;
	loading: boolean;
	disabled: boolean;
	onSubscribe: () => void;
}) {
	const i18n = useI18nContext().LL["new-ui"];
	const formatPrice = useFormatPrice();
	return (
		<PlanCard
			plan={plan}
			action={
				<Button size="lg" className="w-full font-breit font-normal" disabled={!available || disabled || loading} onClick={onSubscribe}>
					{loading && <LoaderCircle className="size-4 animate-spin" />}
					{available ? i18n.OD_SUBS_SUBSCRIBE({ price: formatPrice(plan.priceCents) }) : i18n.OD_SUBS_UNAVAILABLE()}
				</Button>
			}
		/>
	);
}

function SubscriptionCard({
	subscription,
	loading,
	disabled,
	onPay,
}: {
	subscription: Subscription;
	loading: boolean;
	disabled: boolean;
	onPay: () => void;
}) {
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const formatPrice = useFormatPrice();
	const formatDate = useFormatDate();
	const [cancel, isCanceling] = useAsyncMutation<creditsOneDeliverySubscriptionCancelMutation>(cancelMutation);
	const [resume, isResuming] = useAsyncMutation<creditsOneDeliverySubscriptionResumeMutation>(resumeMutation);
	const [confirmCancel, setConfirmCancel] = useState(false);
	const { plan, status, cancelAtPeriodEnd, currentPeriodEnd } = subscription;
	const paymentDue = PAYMENT_DUE.includes(status);
	// An unpaid first subscription is canceled right away instead of at the period end.
	const unpaid = status === "INCOMPLETE";
	const periodEnd = currentPeriodEnd ? formatDate(currentPeriodEnd) : "";

	const run = async (action: () => Promise<unknown>, message: string) => {
		try {
			await action();
			toast.success(message);
			await router.invalidate();
		} catch (error) {
			toast.error(i18n.OD_SUBS_FAILED(), { description: error instanceof Error ? error.message : undefined });
		}
	};

	return (
		<CardFrame
			footer={
				<>
					{paymentDue && (
						<Button size="lg" className="w-full font-breit font-normal" disabled={disabled || loading} onClick={onPay}>
							{loading && <LoaderCircle className="size-4 animate-spin" />}
							{i18n.OD_SUBS_PAY_NOW()}
						</Button>
					)}
					{cancelAtPeriodEnd ? (
						// A deactivated plan cancels its subscriptions for good.
						plan.active && (
							<Button
								size="lg"
								variant="outline"
								className="w-full font-breit font-normal"
								disabled={disabled || isResuming}
								onClick={() => void run(() => resume({ variables: {} }), i18n.OD_SUBS_RESUMED())}
							>
								{isResuming && <LoaderCircle className="size-4 animate-spin" />}
								{i18n.OD_SUBS_RESUME()}
							</Button>
						)
					) : (
						<Button
							size="lg"
							variant="ghost"
							className="w-full font-breit font-normal text-muted-foreground"
							disabled={disabled || isCanceling}
							onClick={() => setConfirmCancel(true)}
						>
							{isCanceling && <LoaderCircle className="size-4 animate-spin" />}
							{i18n.OD_SUBS_CANCEL()}
						</Button>
					)}
				</>
			}
		>
			<div className="flex flex-col gap-1 px-8">
				<p className="text-sm font-medium text-muted-foreground">{i18n.OD_SUBS_YOURS()}</p>
			</div>
			<CardHeading title={plan.title} price={formatPrice(plan.priceCents)} unit={i18n.OD_SUBS_PER_MONTH()} />
			<div className="flex flex-col gap-4 px-8">
				<Feature text={i18n.OD_SUBS_COUNT({ count: plan.credits })} />
				{paymentDue ? (
					<p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
						{unpaid ? i18n.OD_SUBS_PAYMENT_PENDING() : i18n.OD_SUBS_PAYMENT_DUE()}
					</p>
				) : (
					periodEnd && (
						<p className="text-sm text-muted-foreground">
							{cancelAtPeriodEnd ? i18n.OD_SUBS_ENDS({ date: periodEnd }) : i18n.OD_SUBS_RENEWS({ date: periodEnd })}
						</p>
					)
				)}
				{cancelAtPeriodEnd && !plan.active && <p className="text-sm text-muted-foreground">{i18n.OD_SUBS_PLAN_RETIRED()}</p>}
			</div>
			<AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{i18n.OD_SUBS_CANCEL()}</AlertDialogTitle>
						<AlertDialogDescription>
							{unpaid ? i18n.OD_SUBS_CANCEL_UNPAID_CONFIRM() : i18n.OD_SUBS_CANCEL_CONFIRM({ date: periodEnd })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{i18n.OD_SUBS_RESUME()}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setConfirmCancel(false);
								void run(
									() => cancel({ variables: {} }),
									unpaid ? i18n.OD_SUBS_CANCELED_UNPAID() : i18n.OD_SUBS_CANCELED({ date: periodEnd })
								);
							}}
						>
							{i18n.OD_SUBS_CANCEL()}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</CardFrame>
	);
}

/** Confirms a PaymentIntent, or the SetupIntent of a free offer, which saves the card without charging it. */
function PaymentForm({
	intent,
	returnParams,
	onPaid,
}: {
	intent: Checkout["intent"];
	returnParams: Record<string, string>;
	onPaid: () => void;
}) {
	const i18n = useI18nContext().LL["new-ui"];
	const stripe = useStripe();
	const elements = useElements();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const failed = intent === "SETUP" ? i18n.OD_CREDITS_SETUP_FAILED() : i18n.OD_CREDITS_PAYMENT_FAILED();

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		if (!stripe || !elements || isSubmitting) return;
		setIsSubmitting(true);
		try {
			const returnUrl = new URL("/one-delivery/credits", window.location.origin);
			for (const [key, value] of Object.entries(returnParams)) returnUrl.searchParams.set(key, value);
			const confirmParams = { return_url: returnUrl.toString() };
			const result =
				intent === "SETUP"
					? await stripe.confirmSetup({ elements, confirmParams, redirect: "if_required" })
					: await stripe.confirmPayment({ elements, confirmParams, redirect: "if_required" });
			if (result.error) {
				toast.error(result.error.message ?? failed);
				return;
			}
			onPaid();
		} catch (error) {
			console.error(error);
			toast.error(failed);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form className="flex flex-col gap-4" onSubmit={(event) => void handleSubmit(event)}>
			<PaymentElement />
			<Button type="submit" size="lg" className="w-full font-breit font-normal" disabled={!stripe || !elements || isSubmitting}>
				{isSubmitting && <LoaderCircle className="size-4 animate-spin" />}
				{intent === "SETUP" ? i18n.OD_CREDITS_SETUP_SUBMIT() : i18n.OD_CREDITS_PAY()}
			</Button>
		</form>
	);
}
